import React from 'react';
import ActivityChart from './ActivityChart';
import usePersistentToggle from './usePersistentToggle';

// Helper voor voortgangsbalken (zoals in PDF)
const kleuren = {
  goed: '#4caf50', // groen
  redelijk: '#ffc107', // geel
  matig: '#f44336', // rood
  niet: '#f44336', // rood
  correct: '#4caf50',
  incorrect: '#f44336',
  expert: '#4caf50',
  gevorderd: '#ffc107',
  beginner: '#f44336',
};

function ProgressBar({ title, details, total, labels, forceGray }) {
  // details: { goed: x, redelijk: y, ... }
  const barW = 400;
  const barH = 22;
  let totalFilled = 0;
  const order = [
    'goed', 'correct', 'expert',
    'redelijk', 'gevorderd',
    'nee', 'matig', 'incorrect', 'beginner',
  ];
  const keys = [
    ...order.filter(k => Object.keys(details).includes(k)),
    ...Object.keys(details).filter(k => !order.includes(k))
  ];
  const totalKeys = keys.reduce((acc, k) => acc + (details[k] || 0), 0);
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-blue-700">{title}</span>
        <span className="text-sm text-gray-600">{totalKeys}/{total}</span>
      </div>
      <div className="relative w-full max-w-xl" style={{ height: barH }}>
        <div className="flex rounded overflow-hidden shadow-inner" style={{ height: barH, width: barW }}>
          {forceGray ? (
            <div style={{ width: barW, background: '#e0e0e0', height: barH }} title="Niet ingevuld"></div>
          ) : (
            <>
              {keys.map((key, i) => {
                const val = details[key] || 0;
                if (val === 0) return null;
                const w = (val / total) * barW;
                totalFilled += val;
                return (
                  <div key={key} style={{ width: w, background: kleuren[key.toLowerCase()] || '#9e9e9e', height: barH }} title={`${labels[key] || key}: ${val}`}></div>
                );
              })}
              {totalFilled < total && (
                <div style={{ width: ((total - totalFilled) / total) * barW, background: '#e0e0e0', height: barH, position: 'relative' }} title={`Niet ingevuld: ${total - totalFilled}`}></div>
              )}
            </>
          )}
        </div>
        {/* Bolletjes en labels */}
        <div className="flex gap-6 mt-2 flex-wrap">
          {keys.map((key) => (
            <span key={key} className="flex items-center text-xs" style={{ color: kleuren[key.toLowerCase()] || '#757575' }}>
              <span className="inline-block w-3 h-3 rounded-full mr-1" style={{ background: kleuren[key.toLowerCase()] || '#757575' }}></span>
              {labels[key] || key}: {details[key] || 0}
            </span>
          ))}
          {(forceGray || totalFilled < total) && (
            <span className="flex items-center text-xs text-gray-500">
              <span className="inline-block w-3 h-3 rounded-full mr-1 bg-gray-300"></span>
              Niet ingevuld: {forceGray ? total : total - totalFilled}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const VoortgangSection = ({
  onDownloadData,
  isDownloading,
  overallProgress,
  progressData,
  onNavigateIntroductie
}) => {
  const [showIntro, toggleIntro] = usePersistentToggle('voortgang_intro', true);

  // Labels per onderdeel
  const labels = {
    goed: 'Goed',
    redelijk: 'Redelijk',
    niet: 'Niet',
    matig: 'Matig',
    correct: 'Correct',
    incorrect: 'Incorrect',
    expert: 'Expert',
    gevorderd: 'Gevorderd',
    beginner: 'Beginner',
  };

  return (
    <div>
      {/* Gele waarschuwing bovenaan */}
      <div className="bg-yellow-200 border-l-4 border-yellow-500 p-4 rounded mb-4 flex items-center justify-between">
        <span className="font-semibold text-yellow-900">
          Let op: Je voortgang wordt alleen lokaal op deze computer opgeslagen. Bij wissen van browserdata of gebruik op een ander apparaat kan je voortgang verloren gaan. <a href="#" className="underline text-blue-800 hover:text-blue-900" onClick={e => { e.preventDefault(); if (onNavigateIntroductie) onNavigateIntroductie(); }}>Lees meer bij Introductie & Werkwijze</a>.
        </span>
      </div>
      {/* Voortgangscirkel groot */}
      <div className="flex flex-col items-center mb-8">
        <div className="mb-2 text-lg font-semibold text-purple-700">Totale voortgang</div>
        <div className="relative flex items-center justify-center" style={{ width: 120, height: 120 }}>
          {(() => {
            const radius = 54;
            const circumference = 2 * Math.PI * radius;
            const progress = Math.max(0, Math.min(1, overallProgress / 100));
            return (
              <svg width="120" height="120" viewBox="0 0 120 120" className="rotate-[-90deg]">
                <circle
                  cx="60"
                  cy="60"
                  r={radius}
                  fill="none"
                  stroke="#e5e7eb"
                  strokeWidth="10"
                />
                <circle
                  cx="60"
                  cy="60"
                  r={radius}
                  fill="none"
                  stroke="#6b21a8"
                  strokeWidth="10"
                  strokeDasharray={circumference}
                  strokeDashoffset={circumference * (1 - progress)}
                  style={{ transition: 'stroke-dashoffset 0.5s' }}
                />
              </svg>
            );
          })()}
          <span className="absolute text-3xl font-bold text-purple-700">{Math.round(overallProgress)}%</span>
        </div>
        <div className="mt-4 text-center text-gray-700 max-w-md">
          Je voortgangspercentage is het gemiddelde van alle onderdelen. Een onderdeel telt voor de helft mee als je eraan bent begonnen, en voor 100% als je alles hebt afgerond.
        </div>
      </div>
      <div className="mb-4">
        <button
          className="text-blue-700 font-semibold mb-2 focus:outline-none flex items-center gap-2"
          onClick={toggleIntro}
          aria-expanded={showIntro}
          aria-controls="voortgang-intro"
        >
          {showIntro ? '▼' : '►'} Inleiding
        </button>
        {showIntro && (
          <div id="voortgang-intro" className="bg-blue-50 p-4 rounded-lg shadow-sm">
            <p className="text-gray-700 leading-relaxed">
              In dit hoofdstuk krijg je inzicht in je voortgang. Je ziet per onderdeel hoeveel je hebt afgerond en met welke score. <b>Je hoeft niet alles afgerond te hebben.</b> Kies wat bij jouw niveau en leerdoelen past. Herhaling is één van de krachtigste leerstrategieën: door regelmatig terug te keren naar de stof, vergroot je je kennis en vaardigheden duurzaam.
            </p>
          </div>
        )}
      </div>
      {/* Horizontale voortgangsbalken per onderdeel */}
      <div className="mb-10 mt-10">
        <h3 className="text-xl font-semibold text-blue-700 mb-2">Je totale voortgang per onderdeel</h3>
        {progressData && (
          <>
            <ProgressBar title="De Basis (Braindump)" details={progressData.braindumpDetails} total={progressData.braindumpTotal} labels={labels} forceGray={progressData.braindumpTotal === 0} />
            <ProgressBar title="Begrippen Trainer" details={progressData.flashcardScores} total={progressData.flashcardTotal} labels={labels} />
            <ProgressBar title="MC Vragen" details={progressData.mcDetails} total={progressData.mcTotal} labels={labels} />
            <ProgressBar title="Check je Kennis" details={progressData.uitlegDetails} total={progressData.uitlegTotal} labels={labels} />
            <ProgressBar title="Klinisch Redeneren" details={progressData.toepassenDetails} total={progressData.toepassenTotal} labels={labels} />
          </>
        )}
      </div>
      {/* Staafdiagrammen met uitleg */}
      <div className="mb-10 mt-10 pt-10">
        <h3 className="text-xl font-semibold text-blue-700 mb-2">Bekijk hier je activiteiten in de tijd</h3>
        <p className="text-gray-700 mb-4">De grafieken hieronder laten zien wanneer je actief bent geweest in deze module. <b>Herhalen is één van de krachtigste leerstrategieën:</b> door regelmatig terug te keren naar de stof, vergroot je je kennis en vaardigheden duurzaam.</p>
        <ActivityChart />
      </div>
      <div className="mt-10">
        <h3 className="text-lg font-semibold text-blue-700 mb-2">Download je opgeslagen data</h3>
        <p className="text-gray-600 mb-4">
          Je kunt hier al je opgeslagen voortgang en antwoorden downloaden als een JSON-bestand. Dit kun je bijvoorbeeld gebruiken om in een AI-chatbot (zoals ChatGPT) je eigen analyses, visualisaties of berekeningen te maken. Zo kun je je voortgang op een manier bekijken die het beste bij jou past!
        </p>
        <button
          onClick={onDownloadData}
          disabled={isDownloading}
          className={`bg-green-600 text-white py-3 px-6 rounded-md transition-colors w-full ${isDownloading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700'}`}
        >
          {isDownloading ? 'Data wordt gedownload...' : 'Download alle data'}
        </button>
      </div>
    </div>
  );
};

export default VoortgangSection; 